import {Trans} from '@lingui/macro';
import {isEqual} from 'lodash';
import {DateTime} from 'luxon';
import React, {FC} from 'react';
import styled from 'styled-components';

import {intersperseNodes} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {arrayToggle} from '../../../../../libs/shared/helpers/src/utilities/arrayHelpers';
import {Toggle} from '../../../../../libs/shared/ui/src/components/booleanInputs/toggle';
import {DropdownBoxCoordinator} from '../../../../../libs/shared/ui/src/components/boxedInputs/dropdownBoxCoordinator';
import {Button} from '../../../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {IconButton} from '../../../../../libs/shared/ui/src/components/buttons/iconButton';
import {SimpleDropdown} from '../../../../../libs/shared/ui/src/components/dropdowns/simpleDropdown';
import {SimpleDropdownItemWithCheckbox} from '../../../../../libs/shared/ui/src/components/dropdowns/simpleDropdownItemWithCheckbox';
import {VisualSizesEnum} from '../../../../../libs/shared/ui/src/styles/commonStyles';
import {fontSizes, fontWeights} from '../../../../../libs/shared/ui/src/styles/fontStyles';
import {useLocalize} from '../../../../../srcCommon/internationalization/internationalizationContext';
import {TimeComboBox} from '../../../../components/common/dateTime/timeComboBox';
import {TimezoneDropdownBox} from '../../../../components/inboxes/composer/scheduling/meetingsettings/timezoneDropdownBox';
import {formatDate} from '../../../../helpers/dateTimeHelpers';
import {useAppSelector} from '../../../../helpers/reduxHelpers';
import {ChannelFrontChatModel} from '../../../../models/api/channelModel';
import {getTimeFormat} from '../../../../store/data/preferences/preferencesSelectors';
import {Localizer} from '../../../../store/stateless/internationalizationSelectors';
import {SettingsFieldLine} from '../../../common/settingsFieldLine';
import {SettingsCommonChannelFieldProps} from '../settingsCommonChannelFields';

/*
 *.
 */

interface SettingsOfflineHoursFieldProps extends SettingsCommonChannelFieldProps<ChannelFrontChatModel> {}

/*
 * Style.
 */

const StyledOfflineHoursContentDiv = styled.div`
  display: grid;
  grid-auto-flow: row;
  grid-auto-rows: auto;
  grid-row-gap: 20px;
  justify-items: start;
`;

const StyledOfflineHoursRowDiv = styled.div`
  display: grid;
  width: 600px;
  grid-template-columns: 200px 1fr 128px 1fr 128px 1fr;
  align-items: center;
`;

const StyledOfflineHourLabelDiv = styled.div`
  color: ${(p) => p.theme.greys.shade60};
  font-size: ${fontSizes.medium};
  font-weight: ${fontWeights.normal};
  text-align: center;
`;

const StyledIconButtonDiv = styled.div`
  display: flex;
  justify-content: flex-end;
`;

/*
 * Component.
 */

const DAYS = [0, 1, 2, 3, 4, 5, 6];

export const SettingsOfflineHoursField: FC<SettingsOfflineHoursFieldProps> = (props) => {
  const {onPreferencesChange, edited} = props;
  const localize = useLocalize();
  const timeFormat = useAppSelector(getTimeFormat);

  const addOfflineHourRow = () => {
    onPreferencesChange({
      widgetOfflineHours: {
        ...edited.preferences.widgetOfflineHours,
        hours: [
          ...edited.preferences.widgetOfflineHours.hours,
          {
            days: [1, 2, 3, 4, 5],
            timerange: {
              start_time: DateTime.fromObject({hour: 17, minute: 30}).toFormat('HH:mm'),
              end_time: DateTime.fromObject({hour: 7, minute: 30}).toFormat('HH:mm'),
            },
          },
        ],
      },
    });
  };

  const updateRowAtIndex = (
    newRow: ChannelFrontChatModel['preferences']['widgetOfflineHours']['hours'][number],
    index: number,
  ) => {
    const newHours = [...edited.preferences.widgetOfflineHours.hours];

    newHours[index] = newRow;

    onPreferencesChange({
      widgetOfflineHours: {
        ...edited.preferences.widgetOfflineHours,
        hours: newHours,
      },
    });
  };

  const deleteRowAtIndex = (index: number) => {
    const newHours = edited.preferences.widgetOfflineHours.hours.filter(
      (_h, uniqueIndex) => uniqueIndex !== index,
    );
    onPreferencesChange({
      widgetOfflineHours: {
        ...edited.preferences.widgetOfflineHours,
        hours: newHours,
      },
    });
  };

  return (
    <>
      <SettingsFieldLine
        title={<Trans id="settingschannel-offlinehours-title">Enable offline hours</Trans>}
        subtitle={
          <Trans id="settingschannel-offlinehours-subtitle">
            Choose when your widget is hidden from your site
          </Trans>
        }
        content={
          <Toggle
            isChecked={edited.preferences.widgetOfflineHours.enabled}
            onChange={(v) => {
              onPreferencesChange({
                widgetOfflineHours: {
                  ...edited.preferences.widgetOfflineHours,
                  enabled: v,
                },
              });
            }}
          />
        }
      />
      {edited.preferences.widgetOfflineHours.enabled && (
        <StyledOfflineHoursContentDiv>
          <TimezoneDropdownBox
            onTimezoneSelect={(timezone) => {
              onPreferencesChange({
                widgetOfflineHours: {
                  ...edited.preferences.widgetOfflineHours,
                  timezone,
                },
              });
            }}
            timezone={edited.preferences.widgetOfflineHours.timezone}
            label={<Trans id="time-zone">Time zone</Trans>}
          />
          {edited.preferences.widgetOfflineHours.hours.map((hourRow, index) => (
            // eslint-disable-next-line react/no-array-index-key
            <StyledOfflineHoursRowDiv key={index}>
              <DropdownBoxCoordinator
                value={renderDaysDescription(hourRow.days, localize)}
                renderDropdown={(popoverProps) => (
                  <SimpleDropdown {...popoverProps} width={200}>
                    {DAYS.map((day) => (
                      <SimpleDropdownItemWithCheckbox
                        onSelect={(e) => {
                          e.preventDefault();
                          const newDays = arrayToggle(hourRow.days, day);
                          updateRowAtIndex(
                            {
                              ...hourRow,
                              days: newDays,
                            },
                            index,
                          );
                        }}
                        isChecked={hourRow.days.includes(day)}
                      >
                        {formatDate(localize, DateTime.fromObject({weekday: day + 1}), {weekday: 'long'})}
                      </SimpleDropdownItemWithCheckbox>
                    ))}
                  </SimpleDropdown>
                )}
              />
              <StyledOfflineHourLabelDiv>
                <Trans id="from">from</Trans>
              </StyledOfflineHourLabelDiv>
              <TimeComboBox
                timeFormat={timeFormat}
                onTimeSelect={(newDateTime) => {
                  if (!newDateTime) {
                    return;
                  }
                  updateRowAtIndex(
                    {
                      ...hourRow,
                      timerange: {
                        ...hourRow.timerange,
                        start_time: newDateTime.toFormat('HH:mm'),
                      },
                    },
                    index,
                  );
                }}
                selectedTime={DateTime.fromFormat(hourRow.timerange.start_time, 'HH:mm')}
              />
              <StyledOfflineHourLabelDiv>
                <Trans id="to">to</Trans>
              </StyledOfflineHourLabelDiv>
              <TimeComboBox
                timeFormat={timeFormat}
                selectedTime={DateTime.fromFormat(hourRow.timerange.end_time, 'HH:mm')}
                onTimeSelect={(newDateTime) => {
                  if (!newDateTime) {
                    return;
                  }
                  updateRowAtIndex(
                    {
                      ...hourRow,
                      timerange: {
                        ...hourRow.timerange,
                        end_time: newDateTime.toFormat('HH:mm'),
                      },
                    },
                    index,
                  );
                }}
              />
              <StyledIconButtonDiv>
                <IconButton
                  iconName="trash"
                  onClick={() => {
                    deleteRowAtIndex(index);
                  }}
                  label=""
                />
              </StyledIconButtonDiv>
            </StyledOfflineHoursRowDiv>
          ))}

          <Button
            variant={ButtonVariantsEnum.LINK}
            leftIcon="newTiny"
            size={VisualSizesEnum.SMALL}
            onClick={addOfflineHourRow}
          >
            <Trans id="settingschannel-addofflinehours">Add offline hours</Trans>
          </Button>
        </StyledOfflineHoursContentDiv>
      )}
    </>
  );
};

function valuesAreConsecutives(array: ReadonlyArray<number>) {
  return array.every((n, i, list) => (i === 0 ? true : list[i - 1] === n - 1));
}

function renderDaysDescription(days: ReadonlyArray<number>, localize: Localizer) {
  const sorted = [...days].sort();

  if (sorted.length === 1) {
    return formatDate(localize, DateTime.fromObject({weekday: sorted[0] + 1}), {weekday: 'long'});
  }

  if (isEqual([0, 6], sorted)) {
    return <Trans id="weekend">Weekend</Trans>;
  }

  if (isEqual([1, 2, 3, 4, 5], sorted)) {
    return <Trans id="weekdays">Weekdays</Trans>;
  }

  if (valuesAreConsecutives(sorted)) {
    const start = formatDate(localize, DateTime.fromObject({weekday: sorted[0] + 1}), {weekday: 'short'});
    const end = formatDate(localize, DateTime.fromObject({weekday: sorted[sorted.length - 1] + 1}), {
      weekday: 'short',
    });

    return (
      <Trans id="daterange">
        {start} to {end}
      </Trans>
    );
  }

  return intersperseNodes(
    sorted.map((d) => ({
      node: formatDate(localize, DateTime.fromObject({weekday: d + 1}), {weekday: 'short'}),
      key: String(d),
    })),
    () => ', ',
  );
}
